import { useState } from "react";
import type { User } from "../../../types/User";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { FiMenu, FiX } from "react-icons/fi";
import { FaArrowRight } from "react-icons/fa";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { logoutUser } from "../../../store/common/authSlice";
import { stopImpersonation } from "../../../store/superAdmin/impersonationSlice";
import { getBrandLogoAlt, getBrandLogoSrc, isUrisBrand, isVelvetUiActive } from "../../../utils/brandLogo";
import TopBar from "./TopBar";

// Safely declare customNavigate on the window object for TypeScript
declare global {
  interface Window {
    customNavigate?: (targetPath: string | number) => void;
  }
}

const Navbar = ({
  handleGoogleLogin,
  rel,
}: {
  handleGoogleLogin: () => void;
  rel: string | null;
}) => {
  const partnerText = rel || "";
  const [isOpen, setIsOpen] = useState(false);
  const [portalModal, setPortalModal] = useState<null | "cases" | "smile">(null);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const isAuth = useAppSelector((state) => state.auth.isAuth);
  const user = useAppSelector((state) => state.auth.user as User | null);
  const { isImpersonating } = useAppSelector((state) => state.impersonation);

  const isSuperAdmin = user?.role?.toLowerCase() === "super admin";
  const hasCasesAccess = !!user?.verified || isSuperAdmin;
  const hasSmileAccess = !!user?.verifySmile || isSuperAdmin;
  const hasNoPortalAccess = isAuth && !!user && !hasCasesAccess && !hasSmileAccess;
  const isVelvet = isVelvetUiActive(user, location.pathname);
  const isUris = isUrisBrand(user);

  const handleLogout = async () => {
    setIsOpen(false);
    if (isImpersonating) {
      try {
        await dispatch(stopImpersonation()).unwrap();
        // Hard refresh so session restores as Super Admin
        window.location.href = "/dashboard/profile";
      } catch (error) {
        console.error("Error during impersonation stop:", error);
      }
      return;
    }
    dispatch(logoutUser());
    navigate("/");
  };
  const needsProfileOnboarding =
    isAuth &&
    user?.role === "User" &&
    !user.verified &&
    !user.verifySmile;

  // Unified routing director that honors interceptor locks
  const executeNavigation = (fallbackUrl: string) => {
    if (window.customNavigate) {
      window.customNavigate(fallbackUrl);
    } else {
      navigate(fallbackUrl);
    }
  };

  const handleClick = () => {
    if (!isAuth) {
      if (window.customNavigate) {
        window.customNavigate("/dashboard");
      } else {
        navigate("/dashboard");
      }
      handleGoogleLogin();
    } else if (needsProfileOnboarding) {
      executeNavigation("/dashboard/profile");
    } else if (hasCasesAccess) {
      executeNavigation("/dashboard/profile");
    } else if (hasSmileAccess) {
      executeNavigation("/dashboard/smiledesign/generate");
    } else {
      executeNavigation("/dashboard/profile");
    }
  };

  // Portal switcher: route to the portal when the user has access,
  // otherwise prompt them to contact an admin.
  const handlePortalClick = (portal: "cases" | "smile") => {
    setIsOpen(false);
    if (portal === "cases") {
      if (hasCasesAccess) executeNavigation("/dashboard/profile");
      else setPortalModal("cases");
    } else {
      if (hasSmileAccess) executeNavigation("/dashboard/smiledesign/generate");
      else setPortalModal("smile");
    }
  };

  const toggleMenu = () => setIsOpen(!isOpen);

  const blackRoutes = [
    "/",
    "/services",
    "/about",
    "/contact",
    "/careers",
    "/faq",
    "/terms-conditions",
    "/privacy-policy",
    "/add/testimonial",
  ];
  const isBlackBg = blackRoutes.includes(location.pathname);

  return (
    <>
      {/* Inline Topbar */}
      {!isBlackBg && !isVelvet && !isUris && <TopBar />}

      <nav
        className={`${
          isVelvet ? "bg-[#FCF2E5] text-[#2C2C2C]" : "bg-black text-white"
        } px-[4%] py-2 md:py-2 relative transition-colors duration-300`}
      >
        <div className="flex justify-between items-center">
          <Link to="/" className="hover:opacity-75 flex items-center gap-3 md:gap-4 min-w-0">
            <img
              src={getBrandLogoSrc(user, location.pathname)}
              alt={getBrandLogoAlt(user, location.pathname)}
              className={`h-12 md:h-[5vw] w-auto max-w-[220px] object-contain shrink-0 ${
                user?.partner === "bant" ? "rounded-xl" : ""
              } ${isVelvet ? "bg-transparent" : "w-full"}`}
            />
            {isVelvet && (
              <>
                <span
                  aria-hidden="true"
                  className="hidden sm:block h-8 md:h-10 w-px shrink-0 bg-[#2C2C2C]/70"
                />
                <span className="hidden sm:inline text-[11px] md:text-sm font-medium tracking-[0.18em] uppercase text-[#2C2C2C] whitespace-nowrap">
                  Case Flow
                </span>
              </>
            )}
          </Link>

          <div className="hidden md:flex items-center gap-12 md:gap-[3vw] text-[1.1vw]">
            {!user?.partner && (
              <ul className={`flex gap-8 md:gap-[3vw] ${isBlackBg ? "" : "font-semibold uppercase"}`}>
                <li><Link to="/" className="hover:opacity-75">Home</Link></li>
                <li><Link to="/services" className="hover:opacity-75">Services</Link></li>
                <li><Link to="/faq" className="hover:opacity-75">FAQ</Link></li>
                {!isVelvet && (
                  <>
                    <li><Link to="/about" className="hover:opacity-75">About Us</Link></li>
                    <li><Link to="/contact" className="hover:opacity-75">Contact Us</Link></li>
                    <li><Link to="/careers" className="hover:opacity-75">Careers</Link></li>
                  </>
                )}
              </ul>
            )}

            {isAuth && user && (
              <ul className={`flex gap-8 md:gap-[3vw] ${isBlackBg ? "" : "font-semibold uppercase"}`}>
                {needsProfileOnboarding ? (
                  <li>
                    <button
                      onClick={() => {
                        setIsOpen(false);
                        executeNavigation("/dashboard/profile");
                      }}
                      className="hover:opacity-75 uppercase"
                    >
                      Profile
                    </button>
                  </li>
                ) : (
                  <>
                    {hasSmileAccess && (
                      <li>
                        <button
                          onClick={() => handlePortalClick("smile")}
                          className="hover:opacity-75 uppercase"
                        >
                          Smile Portal
                        </button>
                      </li>
                    )}
                    <li>
                      <button
                        onClick={() => handlePortalClick("cases")}
                        className="hover:opacity-75 uppercase"
                      >
                        Cases Portal
                      </button>
                    </li>
                  </>
                )}
              </ul>
            )}

            {!isAuth && (
              <button
                onClick={handleClick}
                className="flex items-center bg-white text-black gap-2 md:gap-[0.5vw] hover:bg-gray-200 py-2 px-4 md:px-[2vw] md:py-[0.5vw] rounded-full"
              >
                Signup
                {isBlackBg && <FaArrowRight />}
              </button>
            )}

            {hasNoPortalAccess && (
              <button
                onClick={handleLogout}
                className="flex items-center bg-white text-black gap-2 md:gap-[0.5vw] hover:bg-gray-200 py-2 px-4 md:px-[2vw] md:py-[0.5vw] rounded-full"
              >
                Logout
              </button>
            )}
          </div>

          <div className="md:hidden">
            <button onClick={toggleMenu} className="text-2xl">
              {isOpen ? <FiX /> : <FiMenu />}
            </button>
          </div>
        </div>

        {isOpen && (
          <div className="mt-4 md:hidden flex flex-col gap-4 text-[5vw]">
            {!user?.partner && (
              <>
                <Link to="/" className="hover:opacity-75" onClick={toggleMenu}>Home</Link>
                <Link to="/about" className="hover:opacity-75" onClick={toggleMenu}>About</Link>
                <Link to="/services" className="hover:opacity-75" onClick={toggleMenu}>Services</Link>
                <Link to="/faq" className="hover:opacity-75" onClick={toggleMenu}>FAQ</Link>
                <Link to="/contact" className="hover:opacity-75" onClick={toggleMenu}>Contact Us</Link>
              </>
            )}
            {isAuth && user && (
              <>
                {needsProfileOnboarding ? (
                  <button
                    onClick={() => {
                      setIsOpen(false);
                      executeNavigation("/dashboard/profile");
                    }}
                    className="hover:opacity-75 text-left"
                  >
                    Profile
                  </button>
                ) : (
                  <>
                    {hasSmileAccess && (
                      <button
                        onClick={() => handlePortalClick("smile")}
                        className="hover:opacity-75 text-left"
                      >
                        Smile Portal
                      </button>
                    )}
                    <button
                      onClick={() => handlePortalClick("cases")}
                      className="hover:opacity-75 text-left"
                    >
                      Cases Portal
                    </button>
                  </>
                )}
              </>
            )}
            {!isAuth && (
              <button
                onClick={() => {
                  toggleMenu();
                  handleClick();
                }}
                className={`${isBlackBg ? "bg-white text-black hover:bg-gray-200" : "bg-black text-white hover:bg-gray-800"} py-2 px-4 rounded-full w-max`}
              >
                Signup
              </button>
            )}

            {hasNoPortalAccess && (
              <button
                onClick={handleLogout}
                className={`${isBlackBg ? "bg-white text-black hover:bg-gray-200" : "bg-black text-white hover:bg-gray-800"} py-2 px-4 rounded-full w-max`}
              >
                Logout
              </button>
            )}
          </div>
        )}

        <hr
          className={`absolute bottom-0 left-1/2 transform -translate-x-1/2 ${
            isVelvet
              ? "w-[100%] border-[#E8D9C4]"
              : isBlackBg
                ? "w-[95%] border-gray-500"
                : "w-[100%] border-gray-300"
          }`}
        />
      </nav>

      {portalModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 px-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 text-black shadow-2xl">
            <div className="flex justify-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-yellow-100">
                <span className="text-4xl">🔒</span>
              </div>
            </div>

            <h2 className="mt-4 text-center text-xl font-bold text-gray-900">
              {portalModal === "cases" ? "Cases Portal" : "Smile Portal"} Access
              Required
            </h2>

            <p className="mt-3 text-center text-sm text-gray-600">
              You don't have access to the{" "}
              {portalModal === "cases" ? "Cases Portal" : "Smile Portal"} yet.
              Please contact us at{" "}
              <a
                href="mailto:admin@toothsketch.com"
                className="font-semibold text-[#2fa9bf] hover:underline"
              >
                admin@toothsketch.com
              </a>{" "}
              to explore access to this portal.
            </p>

            <div className="mt-6 flex justify-center">
              <button
                onClick={() => setPortalModal(null)}
                className="rounded-lg bg-[#2fa9bf] px-6 py-2.5 font-semibold text-white transition hover:bg-[#258ca0]"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;




// import { useState } from "react";
// import type { User } from "../../../types/User";
// import { Link, useNavigate, useLocation } from "react-router-dom";
// import { FiMenu, FiX } from "react-icons/fi";
// import { FaArrowRight } from "react-icons/fa";
// import { useAppDispatch, useAppSelector } from "../../../store/hooks";
// import { logoutUser } from "../../../store/common/authSlice";
// import TopBar from "./TopBar";

// const Navbar = ({
//   handleGoogleLogin,
//   rel,
// }: {
//   handleGoogleLogin: () => void;
//   rel: string | null;
// }) => {
//   const partnerText = rel || "";
//   const [isOpen, setIsOpen] = useState(false);
//   const navigate = useNavigate();
//   const location = useLocation();
//   const dispatch = useAppDispatch();

//   const isAuth = useAppSelector((state) => state.auth.isAuth);
//   const user = useAppSelector((state) => state.auth.user as User | null);

//   const handleClick = () => {
//     if (window.customNavigate) {
//       window.customNavigate('/dashboard/profile');
//     } else {
//       if (!isAuth) {
//         navigate("/dashboard");
//         handleGoogleLogin();
//       } else if (!user?.verified) {
//         dispatch(logoutUser());
//       } else {
//         navigate("/dashboard/profile");
//       }
//     }
//   };

//   const toggleMenu = () => setIsOpen(!isOpen);

//   const blackRoutes = [
//     "/",
//     "/services",
//     "/about",
//     "/contact",
//     "/careers",
//     "/faq",
//     "/terms-conditions",
//     "/privacy-policy",
//     "/add/testimonial",
//   ];
//   const isBlackBg = blackRoutes.includes(location.pathname);

//   return (
//     <>
//       {/* Inline Topbar */}
//       {!isBlackBg && <TopBar />}

//       <nav
//         className={`bg-black text-white px-[4%] py-2 md:py-2 relative transition-colors duration-300`}
//       >
//         <div className="flex justify-between items-center">
//           <Link to="/" className="hover:opacity-75">
//             <img
//               src={
//                 user?.partner === "bant"
//                   ? "/bantlogo.svg"
//                   : user?.partner === "elemdig"
//                     ? "/elemlogo.svg"
//                     : "/logo.png"
//               }
//               alt="ToothSketch Logo"
//               className={` h-12 md:h-[5vw] w-full ${user?.partner === "bant" ? `rounded-xl` : ``
//                 }`}
//             />
//           </Link>

//           <div className="hidden md:flex items-center gap-12 md:gap-[1.5vw] text-[1.1vw]">
//             {user?.partner ? null : (
//               <ul
//                 className={`flex gap-8 md:gap-[3vw] ${isBlackBg ? "" : "font-semibold uppercase"
//                   } `}
//               >
//                 <li>
//                   <Link to="/" className="hover:opacity-75">
//                     Home
//                   </Link>
//                 </li>
//                 <li>
//                   <Link to="/services" className="hover:opacity-75">
//                     Services
//                   </Link>
//                 </li>
//                 <li>
//                   <Link to="/faq" className="hover:opacity-75">
//                     FAQ
//                   </Link>
//                 </li>
//                 <li>
//                   <Link to="/about" className="hover:opacity-75">
//                     About Us
//                   </Link>
//                 </li>
//                 <li>
//                   <Link to="/contact" className="hover:opacity-75">
//                     Contact Us
//                   </Link>
//                 </li>
//                 <li>
//                   <Link to="/careers" className="hover:opacity-75">
//                     Careers
//                   </Link>
//                 </li>
//                 {(user?.verifySmile || user?.role?.toLowerCase() === "super admin") && (
//                   <li>
//                     <Link
//                       to="/dashboard/smiledesign/generate"
//                       className="hover:opacity-75"
//                     >
//                       Smile Portal
//                     </Link>
//                   </li>
//                 )}
//               </ul>
//             )}

//             <button
//               onClick={handleClick}
//               className={`
//                 flex items-center
//                  bg-white text-black gap-2 md:gap-[0.5vw] hover:bg-gray-200 py-2 px-4 md:px-[2vw] md:py-[0.5vw] rounded-full`}
//             >
//               {isAuth
//                 ?
//                 `${user?.user_name.split(" ")[0]}'s Dashboard`
//                 :
//                 "Signup"}
//               {isBlackBg ? <FaArrowRight /> : null}
//             </button>
//           </div>

//           <div className="md:hidden">
//             <button onClick={toggleMenu} className="text-2xl">
//               {isOpen ? <FiX /> : <FiMenu />}
//             </button>
//           </div>
//         </div>

//         {isOpen && (
//           <div className="mt-4 md:hidden flex flex-col gap-4 text-[5vw]">
//             <Link to="/" className="hover:opacity-75" onClick={toggleMenu}>
//               Home
//             </Link>
//             <Link to="/about" className="hover:opacity-75" onClick={toggleMenu}>
//               About
//             </Link>
//             <Link
//               to="/services"
//               className="hover:opacity-75"
//               onClick={toggleMenu}
//             >
//               Services
//             </Link>
//             <Link to="/faq" className="hover:opacity-75" onClick={toggleMenu}>
//               FAQ
//             </Link>
//             <Link
//               to="/contact"
//               className="hover:opacity-75"
//               onClick={toggleMenu}
//             >
//               Contact Us
//             </Link>
//             <button
//               onClick={handleClick}
//               className={`${isBlackBg
//                 ? "bg-white text-black hover:bg-gray-200"
//                 : "bg-black text-white hover:bg-gray-800"
//                 } py-2 px-4 rounded-full w-max`}
//             >
//               {isAuth ? (user?.verified ? "Dashboard" : "Logout") : "Signup"}
//             </button>
//           </div>
//         )}

//         <hr
//           className={`absolute bottom-0 left-1/2 transform -translate-x-1/2 ${isBlackBg ? " w-[95%] border-gray-500" : "w-[100%] border-gray-300"
//             }`}
//         />
//       </nav>
//     </>
//   );
// };

// export default Navbar;